import React, { useContext, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import Grid from '../../../../base-ui/Grid/index.js';
import { AddAdditionalCostModal } from './modalContent/AddAdditionalCostModal';
import { EditAdditionalCostModal } from './modalContent/EditAdditionalCostModal';
import { DeleteAdditionalCostModal } from './modalContent/DeleteAdditionalCostModal';
import { Typography, Button, Box, Stack } from '@mui/material';
import { useApi } from '../../../../hooks/useApi';
import { throttle } from '../../../../utils/throttle';
import { ConfigContext } from '../../../../base-ui/lib';
import { GridActionsCellItem } from '@mui/x-data-grid';
import AdditionalCostGridToolbar from './AdditionalCostGridToolbar';
import EmptySearchState from '../../../../base-ui/Grid/components/EmptySearchState';

const EmptyStateComponent = () => <EmptySearchState />;

export const AdditionalCosts = () => {
    const { companyId } = useParams();
    const api = useApi();
    const gridRef = React.useRef(null);
    const { config } = useContext(ConfigContext);

    const [row, setSelectedRow] = React.useState({
        _id: '',
        costName: '',
        costAmount: 0,
        costDescription: '',
        companyId: ''
    });
    const [addModalOpen, setAddModalOpen] = React.useState(false);
    const [editModalOpen, setEditModalOpen] = React.useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);

    const [filter, setFilter] = useState('');

    const renderToolbar = useCallback(() => (
        <AdditionalCostGridToolbar
            filter={filter}
            setFilter={setFilter}
            handleRefresh={() => gridRef.current?.onRefresh()}
        />
    ), [filter, setFilter]);

    const duplicateAdditionalCost = throttle(async (rowData) => {
        const additionalCost = { ...rowData };
        if (additionalCost._id) {
            await api.additionalCost.create(additionalCost);
        }
    }, 500);

    const headCells = [
        {
            field: 'costName',
            headerName: 'Name',
            flex: 2,
        },
        {
            field: 'costDescription',
            headerName: 'Description',
            flex: 1.5,
        },
        {
            field: 'costAmount',
            headerName: 'Amount ($)',
            valueFormatter: (value) => {
                const numValue = Number(value);
                if (isNaN(numValue) || value == null) return '$0.00';
                return numValue.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                });
            },
            flex: 1,
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: ({row}) => [
                <GridActionsCellItem
                    key="edit"
                    label="Edit"
                    onClick={() => {
                        setSelectedRow(row);
                        setEditModalOpen(true);
                    }}
                    showInMenu
                />,
                <GridActionsCellItem
                    key="duplicate"
                    label="Duplicate"
                    onClick={() => {
                        duplicateAdditionalCost(row);
                        gridRef.current?.onRefresh();
                    }}
                    showInMenu
                />,
                <GridActionsCellItem
                    key="delete"
                    label="Delete"
                    onClick={() => {
                        setSelectedRow(row);
                        setDeleteModalOpen(true);
                    }}
                    showInMenu
                />,
            ],
            flex: 0.7,
        },
    ];

    return (
        <Box
			display="flex"
			flexDirection="column"
            maxWidth={{ xs: '100%', sm: 600, md: 800, lg: 1000 }}
		>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Stack spacing={1}>
                    <Typography variant="h2" fontWeight={700}>
                        Additional Costs
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Create permits that can be applied to quotes
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="flex-end">
                    {renderToolbar()}
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => setAddModalOpen(true)}
                    >
                        Add New Cost
                    </Button>
                </Stack>
            </Stack>
            <Box sx={{ mt: 2 }}>
                <Grid
                    ref={gridRef}
                    headCells={headCells}
                    filterList={[]}
                    searchEndpont={`${config.uri.base}/additionalCost`}
                    pageSizeOptions={[10, 25, 50]}
                    selectableRows={false}
                    hybridMode={true}
                    initialPageSize={1000}
                    onDataLoad={() => {}}
                    options={{ filter }}
                    customNoRowsOverlay={EmptyStateComponent}
                />
            </Box>
            <EditAdditionalCostModal
                companyId={companyId || ''}
                rowData={row}
                gridRef={gridRef}
                modalOpen={editModalOpen}
                setModalOpen={setEditModalOpen}
                setSelectedRow={setSelectedRow}
            />
            <DeleteAdditionalCostModal
                companyId={companyId || ''}
                rowData={row}
                gridRef={gridRef}
                modalOpen={deleteModalOpen}
                setModalOpen={setDeleteModalOpen}
            />
            <AddAdditionalCostModal
                gridRef={gridRef}
                companyId={companyId || ''}
                modalOpen={addModalOpen}
                setModalOpen={setAddModalOpen}
            />
        </Box>
    );
};
